import { X, Printer } from 'lucide-react';
import {
  clientExpenseAmounts,
  sumClientExpenseAmounts,
  type Event,
  type ExpenseItemWithRelations,
  type EventClientLedger,
} from '@peles/shared';
import { useAppSettingValue } from '../../hooks/useAppSettingValue';
import { printDocument } from '../../utils/printDocument';

export type EventSummaryGroupBy = 'vendor' | 'item';

interface Props {
  event: Event & {
    eventTypeName?: string;
    venueName?: string;
    logoUrl?: string | null;
  };
  expenses: ExpenseItemWithRelations[];
  ledger: EventClientLedger | undefined;
  onClose: () => void;
  /** 'vendor' (default) groups expenses by vendor; 'item' lists each expense item as a line. */
  groupBy?: EventSummaryGroupBy;
}

interface ReportRow {
  key: string;
  name: string;
  vendorName?: string;
  totalPrice: number;
  totalPaid: number;
  balance: number;
  /**
   * What the client still owes the producer for the row — a vendor the
   * producer fronted, or the producer's own invoice. Null when nothing on the
   * row is the producer's to collect (the client pays that vendor directly).
   */
  reimbursement: number | null;
}

function fmtReimbursement(n: number | null) {
  return n == null ? '—' : fmt(n);
}

function fmt(n: number) {
  return new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD' }).format(n);
}

function formatDate(d: Date | string | null | undefined) {
  if (!d) return '—';
  const dt = new Date(d);
  return new Date(dt.getUTCFullYear(), dt.getUTCMonth(), dt.getUTCDate()).toLocaleDateString(
    'en-US',
    { month: 'long', day: 'numeric', year: 'numeric' },
  );
}

function esc(s: string | null | undefined) {
  if (!s) return '';
  return s.replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;');
}

/** The client's open balance to the producer on one line, or null for a direct line. */
function reimbursementOf(item: ExpenseItemWithRelations): number | null {
  const amounts = clientExpenseAmounts(item);
  return amounts.owedToProducer ? amounts.openBalance : null;
}

export function EventSummaryPrintModal({
  event,
  expenses,
  ledger,
  onClose,
  groupBy = 'vendor',
}: Props) {
  const { data: appLogoUrl } = useAppSettingValue('app_logo_s3_key');

  const logoSrc =
    event.logoUrl ?? (typeof appLogoUrl === 'string' ? appLogoUrl : null) ?? '/logo.png';

  const byItem = groupBy === 'item';

  // Build the report rows — either one row per item, or one aggregated row per vendor.
  let rows: ReportRow[];
  if (byItem) {
    rows = expenses.map((item) => ({
      key: item.id,
      name: item.itemName ?? item.vendorCategoryName ?? '—',
      vendorName: item.vendorName ?? 'Other',
      totalPrice: item.price,
      totalPaid: item.totalPaid,
      balance: item.balance,
      reimbursement: reimbursementOf(item),
    }));
  } else {
    const vendorMap = new Map<string, ReportRow>();
    for (const item of expenses) {
      const key = item.vendorId ?? item.vendorName ?? 'Other';
      const display = item.vendorName ?? 'Other';
      const existing = vendorMap.get(key) ?? {
        key,
        name: display,
        totalPrice: 0,
        totalPaid: 0,
        balance: 0,
        reimbursement: null,
      };
      existing.totalPrice += item.price;
      existing.totalPaid += item.totalPaid;
      existing.balance += item.balance;
      const owed = reimbursementOf(item);
      if (owed != null) existing.reimbursement = (existing.reimbursement ?? 0) + owed;
      vendorMap.set(key, existing);
    }
    rows = Array.from(vendorMap.values()).sort((a, b) => b.totalPrice - a.totalPrice);
  }

  // Column counts for empty-state / total-row spans
  const colCount = byItem ? 6 : 5;
  const labelSpan = byItem ? 2 : 1;
  const sectionTitle = byItem ? 'Expenses by Item' : 'Expenses by Vendor';
  const modalTitle = byItem ? 'Itemized Expense Report' : 'Event Summary Report';

  const totalExpenses = expenses.reduce((s, i) => s + i.price, 0);
  const totalPaid = expenses.reduce((s, i) => s + i.totalPaid, 0);
  const openBalance = expenses.reduce((s, i) => s + i.balance, 0);
  const reimbursementDue = sumClientExpenseAmounts(expenses).producerReimbursementDue;
  const totalClientPayments = ledger?.totalReceived ?? 0;
  const clientBalanceDue = ledger?.balanceDue ?? 0;

  const eventDate = event.startAt
    ? (() => {
        const start = formatDate(event.startAt);
        if (event.endAt) {
          const end = formatDate(event.endAt);
          return start === end ? start : `${start} – ${end}`;
        }
        return start;
      })()
    : null;

  function handlePrint() {
    // Match the modal's table header style: no background, just gray text + bottom border
    const thStyle =
      'padding:8px 10px;border-bottom:1px solid #e5e7eb;font-size:11px;color:#6b7280;text-transform:uppercase;letter-spacing:0.05em;font-weight:600;';

    const sectionHeadStyle =
      'font-size:11px;font-weight:700;text-transform:uppercase;letter-spacing:0.06em;color:#6b7280;margin-bottom:10px;';

    const expenseHeaderHtml = byItem
      ? `
          <th style="${thStyle}text-align:left;">Item</th>
          <th style="${thStyle}text-align:left;">Vendor</th>
          <th style="${thStyle}text-align:right;">Total</th>
          <th style="${thStyle}text-align:right;">Paid</th>
          <th style="${thStyle}text-align:right;">Balance</th>
          <th style="${thStyle}text-align:right;">Reimbursement</th>`
      : `
          <th style="${thStyle}text-align:left;">Vendor</th>
          <th style="${thStyle}text-align:right;">Total</th>
          <th style="${thStyle}text-align:right;">Paid</th>
          <th style="${thStyle}text-align:right;">Balance</th>
          <th style="${thStyle}text-align:right;">Reimbursement</th>`;

    const expenseRowsHtml =
      rows.length === 0
        ? `<tr><td colspan="${colCount}" style="padding:12px 10px;color:#9ca3af;font-style:italic;">No expenses recorded.</td></tr>`
        : rows
            .map(
              (row) => `
          <tr>
            <td style="padding:8px 10px;border-bottom:1px solid #f3f4f6;font-weight:500;">${esc(row.name)}</td>
            ${
              byItem
                ? `<td style="padding:8px 10px;border-bottom:1px solid #f3f4f6;color:#6b7280;">${esc(row.vendorName)}</td>`
                : ''
            }
            <td style="padding:8px 10px;border-bottom:1px solid #f3f4f6;text-align:right;">${esc(fmt(row.totalPrice))}</td>
            <td style="padding:8px 10px;border-bottom:1px solid #f3f4f6;text-align:right;color:#15803d;">${esc(fmt(row.totalPaid))}</td>
            <td style="padding:8px 10px;border-bottom:1px solid #f3f4f6;text-align:right;color:#dc2626;">${esc(fmt(row.balance))}</td>
            <td style="padding:8px 10px;border-bottom:1px solid #f3f4f6;text-align:right;color:#b45309;">${esc(fmtReimbursement(row.reimbursement))}</td>
          </tr>`,
            )
            .join('');

    const paymentsHtml =
      !ledger || !ledger.payments || ledger.payments.length === 0
        ? `<tr><td colspan="4" style="padding:12px 10px;color:#9ca3af;font-style:italic;">No client payments recorded.</td></tr>`
        : ledger.payments
            .map(
              (p) => `
          <tr>
            <td style="padding:8px 10px;border-bottom:1px solid #f3f4f6;">${esc(formatDate(p.paidAt))}</td>
            <td style="padding:8px 10px;border-bottom:1px solid #f3f4f6;">${esc(p.paymentMethodName ?? '—')}</td>
            <td style="padding:8px 10px;border-bottom:1px solid #f3f4f6;color:#6b7280;">${esc(p.reference ?? '—')}</td>
            <td style="padding:8px 10px;border-bottom:1px solid #f3f4f6;text-align:right;color:#15803d;">${esc(fmt(p.amount))}</td>
          </tr>`,
            )
            .join('');

    const eventMeta = [
      eventDate,
      (event as any).venueName,
      (event as any).eventTypeName,
      event.status ? event.status.toLowerCase().replace('_', ' ') : null,
    ]
      .filter(Boolean)
      .map((line) => `<div>${esc(String(line))}</div>`)
      .join('');

    const balanceBg = clientBalanceDue > 0 ? '#fef2f2' : '#f0fdf4';
    const balanceBorder = clientBalanceDue > 0 ? '#fecaca' : '#bbf7d0';
    const balanceColor = clientBalanceDue > 0 ? '#dc2626' : '#15803d';

    const html = `<!DOCTYPE html>
<html>
<head>
  <meta charset="utf-8"/>
  <title>${esc(modalTitle)} — ${esc(event.name)}</title>
  <style>
    * { box-sizing: border-box; margin: 0; padding: 0; }
    body { font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Arial, sans-serif; padding: 36px; color: #111827; font-size: 13px; line-height: 1.5; }
    @media print {
      body { padding: 20px; }
      @page { margin: 1.5cm; size: A4; }
    }
  </style>
</head>
<body>

  <!-- Header -->
  <div style="display:flex;align-items:flex-start;gap:20px;padding-bottom:20px;border-bottom:1px solid #e5e7eb;margin-bottom:28px;">
    <img src="${logoSrc}" alt="Logo"
      style="width:64px;height:64px;object-fit:contain;border:1px solid #e5e7eb;border-radius:8px;flex-shrink:0;background:#fff;"
      onerror="this.src='/logo.png'"/>
    <div>
      <div style="font-size:20px;font-weight:700;color:#111827;margin-bottom:4px;">${esc(event.name)}</div>
      <div style="font-size:13px;color:#6b7280;line-height:1.7;">${eventMeta}</div>
    </div>
  </div>

  <!-- Expenses -->
  <div style="margin-bottom:28px;">
    <h2 style="${sectionHeadStyle}">${esc(sectionTitle)}</h2>
    <table style="width:100%;border-collapse:collapse;font-size:13px;">
      <thead>
        <tr>${expenseHeaderHtml}
        </tr>
      </thead>
      <tbody>
        ${expenseRowsHtml}
        ${
          rows.length > 0
            ? `<tr style="background:#f9fafb;font-weight:600;">
          <td colspan="${labelSpan}" style="padding:9px 10px;border-top:2px solid #e5e7eb;">Total</td>
          <td style="padding:9px 10px;border-top:2px solid #e5e7eb;text-align:right;">${esc(fmt(totalExpenses))}</td>
          <td style="padding:9px 10px;border-top:2px solid #e5e7eb;text-align:right;color:#15803d;">${esc(fmt(totalPaid))}</td>
          <td style="padding:9px 10px;border-top:2px solid #e5e7eb;text-align:right;color:#dc2626;">${esc(fmt(openBalance))}</td>
          <td style="padding:9px 10px;border-top:2px solid #e5e7eb;text-align:right;color:#b45309;">${esc(fmt(reimbursementDue))}</td>
        </tr>`
            : ''
        }
      </tbody>
    </table>
  </div>

  <!-- Client Payments -->
  <div style="margin-bottom:28px;">
    <h2 style="${sectionHeadStyle}">Client Payments</h2>
    <table style="width:100%;border-collapse:collapse;font-size:13px;">
      <thead>
        <tr>
          <th style="${thStyle}text-align:left;">Date</th>
          <th style="${thStyle}text-align:left;">Method</th>
          <th style="${thStyle}text-align:left;">Reference</th>
          <th style="${thStyle}text-align:right;">Amount</th>
        </tr>
      </thead>
      <tbody>
        ${paymentsHtml}
        ${
          ledger && ledger.payments && ledger.payments.length > 0
            ? `<tr style="background:#f9fafb;font-weight:600;">
          <td colspan="3" style="padding:9px 10px;border-top:2px solid #e5e7eb;">Total Received</td>
          <td style="padding:9px 10px;border-top:2px solid #e5e7eb;text-align:right;color:#15803d;">${esc(fmt(totalClientPayments))}</td>
        </tr>`
            : ''
        }
      </tbody>
    </table>
  </div>

  <!-- Summary cards — use flex for reliable print rendering -->
  <div style="display:flex;gap:14px;border-top:2px solid #e5e7eb;padding-top:20px;">
    <div style="flex:1;border:1px solid #e5e7eb;border-radius:8px;padding:14px 16px;background:#f9fafb;">
      <div style="font-size:11px;color:#6b7280;text-transform:uppercase;letter-spacing:0.04em;margin-bottom:6px;font-weight:600;">Total Expenses</div>
      <div style="font-size:20px;font-weight:700;color:#111827;">${esc(fmt(totalExpenses))}</div>
    </div>
    <div style="flex:1;border:1px solid #bbf7d0;border-radius:8px;padding:14px 16px;background:#f0fdf4;">
      <div style="font-size:11px;color:#15803d;text-transform:uppercase;letter-spacing:0.04em;margin-bottom:6px;font-weight:600;">Client Received</div>
      <div style="font-size:20px;font-weight:700;color:#166534;">${esc(fmt(totalClientPayments))}</div>
    </div>
    <div style="flex:1;border:1px solid ${balanceBorder};border-radius:8px;padding:14px 16px;background:${balanceBg};">
      <div style="font-size:11px;color:${balanceColor};text-transform:uppercase;letter-spacing:0.04em;margin-bottom:6px;font-weight:600;">Balance Due</div>
      <div style="font-size:20px;font-weight:700;color:${balanceColor};">${esc(fmt(clientBalanceDue))}</div>
    </div>
  </div>

</body>
</html>`;

    // Waits for the logo before the dialog opens.
    printDocument(html);
  }

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/50">
      <div className="bg-white rounded-xl shadow-2xl w-full max-w-4xl max-h-[90vh] flex flex-col">
        {/* Modal header */}
        <div className="flex items-center justify-between px-6 py-4 border-b flex-shrink-0">
          <h2 className="text-base font-semibold text-gray-900">{modalTitle}</h2>
          <div className="flex items-center gap-3">
            <button
              onClick={handlePrint}
              className="flex items-center gap-1.5 px-3 py-1.5 bg-gray-800 text-gray-50 rounded-md text-sm hover:bg-gray-900"
            >
              <Printer size={14} />
              Print / Save PDF
            </button>
            <button
              onClick={onClose}
              className="text-gray-400 hover:text-gray-600"
              aria-label="Close"
            >
              <X size={20} />
            </button>
          </div>
        </div>

        {/* Scrollable preview */}
        <div className="overflow-y-auto flex-1 p-6 space-y-6">
          {/* Header */}
          <div className="flex items-start gap-4 pb-5 border-b">
            <img
              src={logoSrc}
              alt="Logo"
              style={{ width: 64, height: 64, objectFit: 'contain', flexShrink: 0 }}
              className="rounded-lg border border-gray-200 bg-white"
              onError={(e) => {
                (e.currentTarget as HTMLImageElement).src = '/logo.png';
              }}
            />
            <div>
              <h1 className="text-xl font-bold text-gray-900">{event.name}</h1>
              <div className="text-sm text-gray-500 mt-1 space-y-0.5">
                {eventDate && <p>{eventDate}</p>}
                {(event as any).venueName && <p>{(event as any).venueName}</p>}
                {(event as any).eventTypeName && <p>{(event as any).eventTypeName}</p>}
                {event.status && (
                  <p className="capitalize">{event.status.toLowerCase().replace('_', ' ')}</p>
                )}
              </div>
            </div>
          </div>

          {/* Expenses */}
          <div>
            <h3 className="text-xs font-semibold text-gray-500 uppercase tracking-wide mb-3">
              {sectionTitle}
            </h3>
            {rows.length === 0 ? (
              <p className="text-sm text-gray-400">No expenses recorded.</p>
            ) : (
              <table className="w-full text-sm">
                <thead>
                  <tr className="border-b">
                    {byItem && (
                      <th className="text-left py-2 text-xs text-gray-500 font-semibold uppercase">
                        Item
                      </th>
                    )}
                    <th className="text-left py-2 text-xs text-gray-500 font-semibold uppercase">
                      Vendor
                    </th>
                    <th className="text-right py-2 text-xs text-gray-500 font-semibold uppercase">
                      Total
                    </th>
                    <th className="text-right py-2 text-xs text-gray-500 font-semibold uppercase">
                      Paid
                    </th>
                    <th className="text-right py-2 text-xs text-gray-500 font-semibold uppercase">
                      Balance
                    </th>
                    <th
                      className="text-right py-2 text-xs text-gray-500 font-semibold uppercase"
                      title="What the client still owes the producer for vendors the producer paid"
                    >
                      Reimbursement
                    </th>
                  </tr>
                </thead>
                <tbody>
                  {rows.map((row) => (
                    <tr key={row.key} className="border-b border-gray-100">
                      {byItem && <td className="py-2 font-medium">{row.name}</td>}
                      <td className="py-2 font-medium">{byItem ? row.vendorName : row.name}</td>
                      <td className="py-2 text-right">{fmt(row.totalPrice)}</td>
                      <td className="py-2 text-right text-green-700">{fmt(row.totalPaid)}</td>
                      <td className="py-2 text-right text-red-600">{fmt(row.balance)}</td>
                      <td className="py-2 text-right text-amber-700">
                        {fmtReimbursement(row.reimbursement)}
                      </td>
                    </tr>
                  ))}
                  <tr className="font-semibold bg-gray-50">
                    <td className="py-2" colSpan={labelSpan}>
                      Total
                    </td>
                    <td className="py-2 text-right">{fmt(totalExpenses)}</td>
                    <td className="py-2 text-right text-green-700">{fmt(totalPaid)}</td>
                    <td className="py-2 text-right text-red-600">{fmt(openBalance)}</td>
                    <td className="py-2 text-right text-amber-700">{fmt(reimbursementDue)}</td>
                  </tr>
                </tbody>
              </table>
            )}
          </div>

          {/* Client Payments */}
          <div>
            <h3 className="text-xs font-semibold text-gray-500 uppercase tracking-wide mb-3">
              Client Payments
            </h3>
            {!ledger || !ledger.payments || ledger.payments.length === 0 ? (
              <p className="text-sm text-gray-400">No client payments recorded.</p>
            ) : (
              <table className="w-full text-sm">
                <thead>
                  <tr className="border-b">
                    <th className="text-left py-2 text-xs text-gray-500 font-semibold uppercase">
                      Date
                    </th>
                    <th className="text-left py-2 text-xs text-gray-500 font-semibold uppercase">
                      Method
                    </th>
                    <th className="text-left py-2 text-xs text-gray-500 font-semibold uppercase">
                      Reference
                    </th>
                    <th className="text-right py-2 text-xs text-gray-500 font-semibold uppercase">
                      Amount
                    </th>
                  </tr>
                </thead>
                <tbody>
                  {ledger.payments.map((p) => (
                    <tr key={p.id} className="border-b border-gray-100">
                      <td className="py-2">{formatDate(p.paidAt)}</td>
                      <td className="py-2">{p.paymentMethodName}</td>
                      <td className="py-2 text-gray-500">{p.reference ?? '—'}</td>
                      <td className="py-2 text-right text-green-700">{fmt(p.amount)}</td>
                    </tr>
                  ))}
                  <tr className="font-semibold bg-gray-50">
                    <td colSpan={3} className="py-2">
                      Total Received
                    </td>
                    <td className="py-2 text-right text-green-700">{fmt(totalClientPayments)}</td>
                  </tr>
                </tbody>
              </table>
            )}
          </div>

          {/* Summary */}
          <div className="grid grid-cols-3 gap-4 pt-4 border-t">
            <div className="bg-gray-50 rounded-lg p-4">
              <p className="text-xs text-gray-500 mb-1">Total Expenses</p>
              <p className="text-lg font-bold">{fmt(totalExpenses)}</p>
            </div>
            <div className="bg-green-50 rounded-lg p-4">
              <p className="text-xs text-green-700 mb-1">Client Received</p>
              <p className="text-lg font-bold text-green-800">{fmt(totalClientPayments)}</p>
            </div>
            <div className={`rounded-lg p-4 ${clientBalanceDue > 0 ? 'bg-red-50' : 'bg-green-50'}`}>
              <p
                className={`text-xs mb-1 ${clientBalanceDue > 0 ? 'text-red-700' : 'text-green-700'}`}
              >
                Balance Due
              </p>
              <p
                className={`text-lg font-bold ${clientBalanceDue > 0 ? 'text-red-800' : 'text-green-800'}`}
              >
                {fmt(clientBalanceDue)}
              </p>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
